import omit from 'lodash/fp/omit'
import { AlarmSeverityLevels, GetUsageReportFilter, Role } from 'common/api/v1/types'
import { UrlParamCache } from '../redux/reducers/urlParamReducer'
import type {
  AlarmsRequestParams,
  AppliancesRequestParams,
  GroupListRequestParams,
  GroupsRequestParams,
  InputsRequestParams,
  KubernetesNodesRequestParams,
  NetworksRequestParams,
  OutputListRequestParams,
  OutputsRequestParams,
  PaginatedRequestParams,
  PortsRequestParams,
  ServicesRequestParams,
  TransformationProfilesRequestParams,
  TunnelsRequestParams,
  UsageRequestParams,
  UsersRequestParams,
} from '../api/nm-types'
import { serializeFilterValues } from '../api/filterValues'
import { matchPath } from 'react-router'

type UrlParams = object

interface Route<P extends UrlParams = UrlParams> {
  (params?: P): string
  route: string
}

const PARAM_RE = /:[a-zA-Z_]\w*/g
const getRoute = <P extends UrlParams = UrlParams>(
  path: string,
  getDefaultUrlParamsFn?: () => UrlParams | undefined,
): Route<P> => {
  const allParams = (path.match(PARAM_RE) ?? []).map((m) => m.slice(1))
  const route = (urlParams?: P): string => {
    const params = urlParams ?? getDefaultUrlParamsFn?.()
    if (!params) return path
    if (Object.entries(params).some(([key, value]) => allParams.includes(key) && !value)) return ''
    const filled = path.replace(PARAM_RE, (m) =>
      encodeURIComponent(String((params as Record<string, unknown>)[m.slice(1)])),
    )
    const qs = Object.entries(omit(allParams, params))
      .filter(([, v]) => v !== undefined)
      // Array values are multi-value filter params: serialize with the same escaping the list
      // filters use, so a value containing a comma round-trips through parseFilterValues on read.
      .map(
        ([k, v]) => `${k}=${encodeURIComponent(Array.isArray(v) ? serializeFilterValues(v.map(String)) : String(v))}`,
      )
      .join('&')
    if (!qs) return filled
    return `${filled}${path.includes('?') ? '&' : '?'}${qs}`
  }
  route.route = path
  return route
}

const getCreateRoute = (path: string, query?: string) =>
  getRoute(`${path}/${SUB_PATH.NEW}` + (query ? `?${query}` : ''))
const getUpdateRoute = (path: string) => getRoute(`${path}/${SUB_PATH.UPDATE}`)
const getViewRoute = (path: string) => getRoute(`${path}/${SUB_PATH.VIEW}`)
const getCopyRoute = (path: string) => getRoute(`${path}/${SUB_PATH.COPY}`)

export const SUB_PATH = {
  NEW: 'new',
  UPDATE: 'update/:id',
  VIEW: 'view/:id',
  COPY: 'copy/:id',
} as const

const root = {
  alarms: '/alarms',
  alarmLogs: '/alarmLogs',
  audit: '/audit',
  appliances: '/settings/appliances',
  billing: '/settings/billing',
  usage: '/settings/usage',
  usage_new: '/settings/usage_new',
  globalSettings: '/globalSettings',
  overlays: '/overlays',
  transformationProfiles: '/transformationProfiles',
  adInsertion: '/adInsertion',
  adCreatives: '/adInsertion/adCreatives',
  adPods: '/adInsertion/adPods',
  graph: '/graph',
  inputs: '/inputs',
  interfaces: '/settings/interfaces',
  groups: '/settings/groups',
  groupLists: '/settings/groups/lists',
  outputs: '/outputs',
  outputLists: '/outputs/lists',
  overview: '/overview',
  profile: '/profile',
  regions: '/settings/regions',
  service: '/service/:id',
  setting: '/settings',
  status: '/status',
  tunnels: '/settings/tunnels',
  users: '/settings/users',
  services: '/settings/services',
  nodes: '/settings/kubernetesNodes',
  addresses: '/settings/addresses',
  addressMappings: '/settings/addressMappings',
  networks: '/settings/networks',

  // Stream Manager
  stream: '/stream',
  streamProfile: '/stream/profile',

  // Event Manager
  events: '/events',
  eventsProfile: '/events/profile',
} as const
export const ROUTES = root

/**
 * In case some api request can be used not just in IApi instance
 */
export const api = {
  billing: (params: { startDate: Date; endDate: Date; format?: string }) => {
    return getRoute('/api/billing-report')({
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      format: params.format,
    })
  },
  usage: (params: UsageRequestParams) => {
    const filter: GetUsageReportFilter & { serviceType?: string; groupId?: string } = {
      startDate: params.startDate,
      endDate: params.endDate,
      format: params.format,
      type: params.type,
      serviceType: params.serviceType,
      groupId: params.groupId,
    }
    // Use legacy usage-report endpoint which supports CSV downloads for the old Usage page
    return getRoute('/api/usage-report')() + `?q=${encodeURIComponent(JSON.stringify({ filter }))}`
  },
  usage2: (params: UsageRequestParams) => {
    // New usage (v2) endpoint with optional CSV format
    const filter: GetUsageReportFilter & { serviceType?: string; groupId?: string } = {
      startDate: params.startDate,
      endDate: params.endDate,
      format: params.format,
      serviceType: params.serviceType,
      groupId: params.groupId,
    }
    return getRoute('/api/usage')() + `?q=${encodeURIComponent(JSON.stringify({ filter }))}`
  },
  appliance: (params: { id: string }) => getRoute('/api/appliance/:id/proxy')({ id: params.id }),
}

/**
 * All the page routes. Never use constants, add new instances here
 * @returns {string} absolute full path
 */
export const makeRoutes = (getCachedUrlParams: () => UrlParamCache) => ({
  overview: getRoute(root.overview),
  graph: getRoute(root.graph),
  service: getRoute(root.service),
  globalSettings: getRoute(root.globalSettings),
  overlays: getRoute(root.overlays),
  overlaysNew: getCreateRoute(root.overlays),
  overlaysUpdate: getUpdateRoute(root.overlays),
  overlaysCopy: getCopyRoute(root.overlays),

  adInsertion: getRoute(root.adInsertion),
  adAudit: getRoute(`${root.adInsertion}/adAudit`),

  adServers: getRoute(`${root.adInsertion}/adServers`),
  adServersNew: getCreateRoute(`${root.adInsertion}/adServers`),
  adServersUpdate: getUpdateRoute(`${root.adInsertion}/adServers`),
  adServersCopy: getCopyRoute(`${root.adInsertion}/adServers/`),

  adCreatives: getRoute(`${root.adCreatives}`),
  adCreativesNew: getCreateRoute(`${root.adCreatives}`),
  adCreativesUpdate: getUpdateRoute(`${root.adCreatives}`),

  adPods: getRoute(`${root.adPods}`),
  adPodsNew: getCreateRoute(`${root.adPods}`),
  adPodsUpdate: getUpdateRoute(`${root.adPods}`),

  adBreaks: getRoute(`${root.adInsertion}/adBreaks`),
  adBreaksHistory: getRoute(`${root.adInsertion}/adBreaks/history`),
  adBreaksView: getViewRoute(`${root.adInsertion}/adBreaks`),

  adBreakControl: getRoute(`${root.adInsertion}/adBreakControl`),

  inputs: getRoute<Partial<InputsRequestParams>>(root.inputs, () => getCachedUrlParams()['inputs']),
  inputsNew: getCreateRoute(root.inputs),
  inputsUpdate: getUpdateRoute(root.inputs),
  inputsCopy: getRoute(`${root.inputs}/copy/:id`),
  inputsDerive: getCreateRoute(root.inputs, `deriveFrom=:id`),

  outputs: getRoute<Partial<OutputsRequestParams>>(root.outputs, () => getCachedUrlParams()['outputs']),
  outputsNew: getCreateRoute(root.outputs),
  outputsUpdate: getUpdateRoute(root.outputs),
  outputsCopy: getRoute(`${root.outputs}/copy/:id`),

  outputLists: getRoute<Partial<OutputListRequestParams>>(root.outputLists, () => getCachedUrlParams()['outputLists']),
  outputListsNew: getCreateRoute(root.outputLists),
  outputListsUpdate: getUpdateRoute(root.outputLists),

  alarms: getRoute<Partial<AlarmsRequestParams> & { applianceName?: string; alarmTab?: AlarmSeverityLevels }>(
    root.alarms,
    () => getCachedUrlParams()['alarms'],
  ),
  alarmLogs: getRoute(root.alarmLogs),

  appliances: getRoute<Partial<AppliancesRequestParams>>(root.appliances, () => getCachedUrlParams()['appliances']),
  appliancesConfig: getRoute(`${root.appliances}/:id/config`),
  appliancesUpdate: getUpdateRoute(root.appliances),

  groups: getRoute<Partial<GroupsRequestParams>>(root.groups, () => getCachedUrlParams()['groups']),
  groupsNew: getCreateRoute(root.groups),
  groupsUpdate: getUpdateRoute(root.groups),

  groupLists: getRoute<Partial<GroupListRequestParams>>(root.groupLists, () => getCachedUrlParams()['groupLists']),
  groupListsNew: getCreateRoute(root.groupLists),
  groupListsUpdate: getUpdateRoute(root.groupLists),

  users: getRoute<Partial<UsersRequestParams>>(root.users, () => getCachedUrlParams()['users']),
  usersNew: getCreateRoute(root.users),
  usersUpdate: getUpdateRoute(root.users),
  profile: getRoute(root.profile),

  services: getRoute<Partial<ServicesRequestParams>>(root.services, () => getCachedUrlParams()['k8sServices']),
  servicesUpdate: getUpdateRoute(root.services),

  addressMappings: getRoute<Partial<PaginatedRequestParams>>(
    root.addressMappings,
    () => getCachedUrlParams()['addressMappings'],
  ),
  addressMappingsNew: getCreateRoute(root.addressMappings),
  addressMappingsUpdate: getRoute(`${root.addressMappings}/update/:region/:privateIp`),

  interfaces: getRoute<Partial<PortsRequestParams>>(root.interfaces, () => getCachedUrlParams()['interfaces']),
  interfacesUpdate: getUpdateRoute(root.interfaces),

  settings: getRoute(root.setting),

  status: getRoute(root.status),

  tunnels: getRoute<Partial<TunnelsRequestParams>>(root.tunnels, () => getCachedUrlParams()['tunnels']),
  tunnelNew: getCreateRoute(root.tunnels),
  tunnelUpdate: getUpdateRoute(root.tunnels),

  billing: getRoute(root.billing),

  usage: getRoute(root.usage),
  usage_new: getRoute(root.usage_new),

  audit: getRoute(root.audit),

  regions: getRoute<Partial<PaginatedRequestParams>>(root.regions, () => getCachedUrlParams()['regions']),
  regionsNew: getCreateRoute(root.regions),
  regionsUpdate: getRoute(`${root.regions}/update/:id`),
  kubernetesNodes: getRoute<Partial<KubernetesNodesRequestParams>>(root.nodes, () => getCachedUrlParams()['k8sNodes']),
  kubernetesNodesUpdate: getRoute(`${root.nodes}/update/:regionId/node/:name`),

  networks: getRoute<Partial<NetworksRequestParams>>(root.networks, () => getCachedUrlParams()['networks']),
  networkNew: getCreateRoute(root.networks),
  networkUpdate: getUpdateRoute(root.networks),

  // Stream Manager
  stream: getRoute<Record<string, string>>(root.stream, () => getCachedUrlParams()['streamManager']),
  streamProfile: getRoute(root.streamProfile),

  // Event Manager
  events: getRoute(root.events),
  eventsProfile: getRoute(root.eventsProfile),

  // Transformation Profiles
  transformationProfiles: getRoute<Partial<TransformationProfilesRequestParams>>(
    root.transformationProfiles,
    () => getCachedUrlParams()['transformationProfiles'],
  ),
  transformationProfilesNew: getCreateRoute(root.transformationProfiles),
  transformationProfilesUpdate: getUpdateRoute(root.transformationProfiles),
})

export function doesPathBelongTo(path: string, parentPath: string): boolean {
  return !!matchPath({ path: parentPath, end: false }, path)
}

export type Routes = ReturnType<typeof makeRoutes>

/**
 * Single source of truth for page names used by breadcrumbs, sidebar, and global search.
 * Add new pages here when adding new top-level routes.
 * Role-restricted and feature-flagged pages are filtered at search time.
 */
export const PAGE_NAMES = {
  overview: 'Overview',
  inputs: 'Inputs',
  outputs: 'Outputs',
  outputLists: 'Output lists',
  appliances: 'Appliances',
  alarms: 'Alarms',
  alarmLogs: 'Alarm history',
  users: 'Users',
  groups: 'Groups',
  groupLists: 'Group lists',
  regions: 'Regions',
  tunnels: 'Tunnels',
  interfaces: 'Interfaces',
  networks: 'Networks',
  addressMappings: 'Address mappings',
  kubernetesNodes: 'Kubernetes nodes',
  services: 'Kubernetes services',
  overlays: 'Overlays',
  transformationProfiles: 'Transformation profiles',
  audit: 'Audit log',
  billing: 'Billing',
  usage: 'Usage',
  globalSettings: 'Global settings',
  status: 'Status',
  profile: 'Profile',
  adInsertion: 'Ad Insertion',
  adServers: 'Ad Servers',
  adPods: 'Ad Pods',
  adCreatives: 'Ad Creatives',
  adBreaks: 'Ad Breaks',
  adBreakControl: 'Control Center',
  adAudit: 'Ad Audit Log',
  stream: 'Stream Manager',
  events: 'Event Manager',
} satisfies Partial<Record<keyof Routes, string>>

/**
 * Create actions
 */
export type CreateGroup = 'Streaming' | 'Administration' | 'Networking' | 'Transformations' | 'Ad Insertion'

interface BaseCreateAction {
  name: string
  group: CreateGroup
  superOnly?: boolean
  adOnly?: boolean
}

export interface RouteCreateAction extends BaseCreateAction {
  getRoute: (routes: Routes) => string
}

export interface DialogCreateAction extends BaseCreateAction {
  dialogType: 'edge' | 'core'
}

export type CreateAction = RouteCreateAction | DialogCreateAction

export function isDialogAction(action: CreateAction): action is DialogCreateAction {
  return 'dialogType' in action
}

export const CREATE_ACTIONS: CreateAction[] = [
  // Streaming
  { name: 'Input', group: 'Streaming', getRoute: (r) => r.inputsNew() },
  { name: 'Output', group: 'Streaming', getRoute: (r) => r.outputsNew() },
  { name: 'Output List', group: 'Streaming', getRoute: (r) => r.outputListsNew() },
  // Administration
  { name: 'User', group: 'Administration', superOnly: true, getRoute: (r) => r.usersNew() },
  { name: 'Group', group: 'Administration', superOnly: true, getRoute: (r) => r.groupsNew() },
  { name: 'Group List', group: 'Administration', getRoute: (r) => r.groupListsNew() },
  { name: 'Region', group: 'Administration', superOnly: true, getRoute: (r) => r.regionsNew() },
  { name: 'Edge Appliance', group: 'Administration', dialogType: 'edge' },
  { name: 'Core Video Appliance', group: 'Administration', superOnly: true, dialogType: 'core' },
  // Networking
  { name: 'Address Mapping', group: 'Networking', getRoute: (r) => r.addressMappingsNew() },
  { name: 'P2P Tunnel', group: 'Networking', getRoute: (r) => r.tunnelNew() },
  { name: 'Network', group: 'Networking', superOnly: true, getRoute: (r) => r.networkNew() },
  // Transformations
  { name: 'Transformation Profile', group: 'Transformations', getRoute: (r) => r.transformationProfilesNew() },
  { name: 'Overlay', group: 'Transformations', getRoute: (r) => r.overlaysNew() },
  // Ad Insertion
  { name: 'Creative', group: 'Ad Insertion', adOnly: true, getRoute: (r) => r.adCreativesNew() },
  { name: 'Pod', group: 'Ad Insertion', adOnly: true, getRoute: (r) => r.adPodsNew() },
  { name: 'External Ad Server', group: 'Ad Insertion', adOnly: true, getRoute: (r) => r.adServersNew() },
]

export function filterCreateActions(
  actions: CreateAction[],
  userRole: Role,
  adInsertionEnabled: boolean,
): CreateAction[] {
  return actions.filter((action) => {
    if (action.superOnly && userRole !== Role.super) return false
    if (action.adOnly && !adInsertionEnabled) return false
    return true
  })
}

const ADD_APPLIANCE_ACTION_PREFIX = 'action:add-appliance:'

export function addApplianceAction(type: 'edge' | 'core'): string {
  return `${ADD_APPLIANCE_ACTION_PREFIX}${type}`
}

export function parseAddApplianceAction(target: string): 'edge' | 'core' | undefined {
  if (!target.startsWith(ADD_APPLIANCE_ACTION_PREFIX)) return undefined
  return target.slice(ADD_APPLIANCE_ACTION_PREFIX.length) as 'edge' | 'core'
}
